/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import {
  AlertTriangle, CheckCircle2, Clock, Loader2, Mail, Send, XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const previousMonthValue = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const DispatchBilansCard = () => {
  const defaultPeriod = useMemo(previousMonthValue, []);
  const [period, setPeriod] = useState(defaultPeriod);
  const [agents, setAgents] = useState([]);
  const [history, setHistory] = useState([]);
  const [dispatching, setDispatching] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([
        axios.get(`${API}/agent-performance/agents-list`, { headers: auth() }),
        axios.get(`${API}/agent-performance/dispatch-history?limit=6`, { headers: auth() }),
      ]);
      setAgents(a.data || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error('Erreur chargement distribution');
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const triggerDispatch = async () => {
    if (!window.confirm(
      `Envoyer le bilan ${period} à tous les agents ayant un email enregistré ?\n\nLe PDF sera envoyé en pièce jointe via Resend.`,
    )) return;
    setDispatching(true);
    try {
      const r = await axios.post(
        `${API}/agent-performance/dispatch-monthly?period=${period}`,
        {},
        { headers: auth() },
      );
      const d = r.data;
      toast.success(
        `${d.sent_count}/${d.total_agents} bilans envoyés${d.errors_count ? ` · ${d.errors_count} erreur(s)` : ''}${d.skipped_no_email_count ? ` · ${d.skipped_no_email_count} sans email` : ''}`,
        { duration: 6000 },
      );
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur distribution');
    } finally { setDispatching(false); }
  };

  const realAgents = agents.filter((a) => !a.is_test_account);
  const withEmail = realAgents.filter((a) => a.email);
  const withoutEmail = realAgents.filter((a) => !a.email);
  const lastDispatch = history[0];

  return (
    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white" data-testid="dispatch-bilans-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif font-semibold text-zinc-900 flex items-center gap-2">
              <Mail className="w-4 h-4 text-emerald-700" /> Distribution mensuelle des bilans
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              Envoi automatique le <strong>3 du mois à 08:00 UTC</strong> via Resend (PDF en pièce jointe).
            </p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">
            Auto · 3/mois
          </Badge>
        </div>

        {/* Stats agents */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded border border-emerald-200 bg-white p-2">
            <div className="text-zinc-500">Agents avec email</div>
            <div className="text-2xl font-mono font-bold text-emerald-700">{withEmail.length}</div>
          </div>
          <div className="rounded border border-amber-200 bg-amber-50 p-2">
            <div className="text-zinc-500">Sans email (non envoyés)</div>
            <div className="text-2xl font-mono font-bold text-amber-700">{withoutEmail.length}</div>
          </div>
        </div>

        {withoutEmail.length > 0 && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 leading-snug">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Configurez l'email manquant dans <strong>Gestion des utilisateurs</strong> (cliquer sur ✉ aucun email · cliquer pour ajouter sous le téléphone). Sans email :{' '}
            {withoutEmail.slice(0, 3).map((a) => a.name).join(', ')}
            {withoutEmail.length > 3 ? ` et ${withoutEmail.length - 3} autre(s)` : ''}.
          </div>
        )}

        {/* Manual trigger */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-emerald-100">
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-zinc-300 rounded-md px-2 py-1.5 text-sm bg-white"
            data-testid="dispatch-period-input"
          />
          <Button
            size="sm"
            onClick={triggerDispatch}
            disabled={dispatching || withEmail.length === 0}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="dispatch-trigger-btn"
          >
            {dispatching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer maintenant ({withEmail.length})
          </Button>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="pt-2 border-t border-emerald-100">
            <h4 className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium mb-1.5">
              Historique récent
            </h4>
            <ul className="space-y-1 text-xs">
              {history.slice(0, 5).map((h) => (
                <li key={h.id} className="flex items-center gap-2 py-1 border-b border-zinc-100 last:border-0">
                  <Clock className="w-3 h-3 text-zinc-400 shrink-0" />
                  <span className="font-mono text-zinc-700">{h.period}</span>
                  <span className="text-zinc-400 text-[10px]">{(h.dispatched_at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <span className="flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" /> {h.sent_count}
                    </span>
                    {h.errors_count > 0 && (
                      <span className="flex items-center gap-1 text-red-700">
                        <XCircle className="w-3 h-3" /> {h.errors_count}
                      </span>
                    )}
                    <span className="text-zinc-500 text-[10px]">{h.triggered_by?.startsWith('manual') ? 'manuel' : 'auto'}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!lastDispatch && (
          <p className="text-[11px] text-zinc-500 italic">Aucun envoi pour l'instant.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default DispatchBilansCard;
